import React, { useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { AlertOctagon, Trash2, X, Lock } from "lucide-react";

/**
 * Two-stage account deletion flow (Apple Guideline 5.1.1(v)):
 *   Stage 1: "Are you sure?" — plain warning
 *   Stage 2: Final warning + password + type DELETE to confirm
 */
export default function DeleteAccountModal({ onClose }) {
  useBodyScrollLock(true);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState(1);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const confirmDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setBusy(true);
    try {
      await api.delete("/users/me", { data: { password, confirm: "DELETE" } });
      toast.success("Account deleted");
      // Force a full local sign-out and route home.
      localStorage.removeItem("karm_token");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };

  const body = (
    <>
      <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div role="dialog" aria-modal="true" data-testid="delete-account-modal"
           className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-md pointer-events-auto border-4 shadow-2xl overflow-hidden bg-background"
             style={{ borderColor: "#FF3B30" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b-2"
               style={{ backgroundColor: "#FF3B30", color: "#fff", borderBottomColor: "#000" }}>
            <div className="inline-flex items-center gap-2">
              <AlertOctagon className="w-6 h-6" strokeWidth={3} />
              <span className="font-heading text-xl">DELETE ACCOUNT</span>
            </div>
            {!busy && (
              <button data-testid="delete-close" onClick={onClose}
                      className="h-9 w-9 inline-flex items-center justify-center hover:bg-white/20"
                      aria-label="Close">
                <X className="w-5 h-5" strokeWidth={3} />
              </button>
            )}
          </div>

          {/* Body */}
          {stage === 1 && (
            <div className="p-6 space-y-5">
              <p className="text-base font-bold">
                Are you sure you want to delete your account?
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This will remove your login and any personal roster records tied to it. You will be
                signed out immediately.
              </p>
              <div className="flex gap-3">
                <button data-testid="delete-stage1-cancel" onClick={onClose}
                        className="flex-1 h-12 border-2 border-foreground dark:border-safety-yellow font-bold uppercase tracking-wider text-sm hover:bg-foreground hover:text-background transition-colors">
                  Cancel
                </button>
                <button data-testid="delete-stage1-continue" onClick={() => setStage(2)}
                        className="flex-1 h-12 bg-status-expired text-white border-2 border-black font-black uppercase tracking-wider text-sm hover:opacity-90">
                  Continue
                </button>
              </div>
            </div>
          )}

          {stage === 2 && (
            <div className="p-6 space-y-5">
              <div className="border-2 p-3 bg-status-expired/10" style={{ borderColor: "#FF3B30" }}>
                <p className="text-sm font-bold" style={{ color: "#FF3B30" }}>
                  Your account and data will be erased and cannot be recovered.
                </p>
                <p className="text-xs mt-1 text-foreground/80">
                  Would you like to proceed with deletion?
                </p>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                  <Lock className="w-3 h-3" strokeWidth={3} /> Enter your password
                </label>
                <input
                  data-testid="delete-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="industrial-input"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-2 block">
                  Type <b className="text-status-expired">DELETE</b> to confirm
                </label>
                <input
                  data-testid="delete-confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoCapitalize="characters"
                  autoComplete="off"
                  className="industrial-input font-mono tracking-widest"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button data-testid="delete-stage2-back" onClick={() => setStage(1)} disabled={busy}
                        className="flex-1 h-12 border-2 border-foreground dark:border-safety-yellow font-bold uppercase tracking-wider text-sm hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">
                  Back
                </button>
                <button data-testid="delete-confirm"
                        onClick={confirmDelete}
                        disabled={busy || confirmText.trim().toUpperCase() !== "DELETE" || !password}
                        className="flex-1 h-12 bg-status-expired text-white border-2 border-black font-black uppercase tracking-wider text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                  <Trash2 className="w-4 h-4" strokeWidth={3} />
                  {busy ? "Deleting…" : "Delete Forever"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(body, document.body);
}
